import {
  APPROVAL_ACTIONS,
  type ApprovalAction,
  type ApprovalLimitView,
  EXPORT_FIELDS,
  EXPORT_FORMATS,
  type ExportBatchView,
  type ExportColumn,
  type ExportField,
  type ExportPreview,
  type ExportProfileInput,
  type ExportProfileView,
  type MatchSettings,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Download, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime, tenge } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));

/* ───────────────────────── Лимиты согласования (ДС-06) и допуск сверки (ФН-20) ───────────────────────── */

type Subjects = { roles: Array<{ id: string; name: string }>; users: Array<{ id: string; name: string }> };

export function LimitsView() {
  const me = useMe();
  const qc = useQueryClient();
  const money = seesMoney(me.access, 'acts_invoices');
  const list = useQuery({
    queryKey: ['finance', 'approval-limits'],
    queryFn: () => api<{ limits: ApprovalLimitView[]; can: { edit: boolean } }>('/finance/approval-limits'),
    enabled: money,
  });
  const edit = list.data?.can.edit ?? false;
  const subjects = useQuery({
    queryKey: ['finance', 'approval-limits', 'subjects'],
    queryFn: () => api<Subjects>('/finance/approval-limits/subjects'),
    enabled: edit,
  });
  const match = useQuery({
    queryKey: ['finance', 'payment-match'],
    queryFn: () => api<MatchSettings & { can: { edit: boolean } }>('/finance/payment-match'),
    enabled: money,
  });
  const [f, setF] = useState({
    action: 'purchase_approval' as ApprovalAction,
    kind: 'role' as 'role' | 'user',
    subject: '',
    amount: '',
    deviation: '',
  });
  const put = useMutation({
    mutationFn: () =>
      api<ApprovalLimitView[]>('/finance/approval-limits', {
        method: 'PUT',
        body: {
          action: f.action,
          roleId: f.kind === 'role' ? f.subject : null,
          userId: f.kind === 'user' ? f.subject : null,
          maxAmount: f.amount.trim() ? num(f.amount) : null,
          maxDeviationPct: f.action === 'purchase_approval' && f.deviation.trim() ? num(f.deviation) : null,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance', 'approval-limits'] });
      setF({ ...f, subject: '', amount: '', deviation: '' });
      toast('Лимит сохранён');
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/finance/approval-limits/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance', 'approval-limits'] }),
  });
  const [tol, setTol] = useState<{ enabled: boolean; pricePct: string; qtyPct: string } | null>(null);
  const savedTol = match.data;
  const curTol =
    tol ??
    (savedTol
      ? { enabled: savedTol.enabled, pricePct: String(savedTol.pricePct), qtyPct: String(savedTol.qtyPct) }
      : null);
  const putTol = useMutation({
    mutationFn: (v: { enabled: boolean; pricePct: string; qtyPct: string }) =>
      api('/finance/payment-match', {
        method: 'PUT',
        body: { enabled: v.enabled, pricePct: num(v.pricePct), qtyPct: num(v.qtyPct) },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance', 'payment-match'] });
      setTol(null);
      toast('Допуск сверки сохранён');
    },
  });
  if (!money)
    return (
      <EmptyState title="Нужен уровень денег «Акты и счета»">
        Лимиты согласования и допуск сверки видны с этим уровнем денег.
      </EmptyState>
    );
  if (list.isLoading) return <Spinner />;
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Лимиты согласования"
        meta="ДС-06: выше лимита решает следующий согласующий. Нет строки — лимита нет; владелец компании лимитом не ограничен"
      >
        {list.data?.limits.length ? (
          <ul className="divide-y divide-line">
            {list.data.limits.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[14px]">
                <div>
                  <div className="font-medium">
                    {l.userName ?? l.roleName} <Badge tone="neutral">{l.userName ? 'человек' : 'роль'}</Badge>
                  </div>
                  <div className="text-[13px] text-ink-2">
                    {APPROVAL_ACTIONS[l.action]}:{' '}
                    {[
                      l.maxAmount !== null ? `сумма до ${tenge(l.maxAmount)}` : null,
                      l.maxDeviationPct !== null ? `превышение сметы до ${l.maxDeviationPct} %` : null,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
                {edit ? (
                  <Button variant="ghost" aria-label="Убрать лимит" onClick={() => del.mutate(l.id)}>
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[14px] text-ink-2">Лимитов нет: сотрудники с правом согласуют любые суммы.</p>
        )}
        {edit ? (
          <form
            className="mt-3 grid gap-3 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              put.mutate();
            }}
          >
            <Field label="Действие">
              <Select
                value={f.action}
                onChange={(e) => setF({ ...f, action: e.target.value as ApprovalAction })}
              >
                {Object.entries(APPROVAL_ACTIONS).map(([k, t]) => (
                  <option key={k} value={k}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Для кого">
              <Select
                value={f.kind}
                onChange={(e) => setF({ ...f, kind: e.target.value as 'role' | 'user', subject: '' })}
              >
                <option value="role">роли</option>
                <option value="user">человека</option>
              </Select>
            </Field>
            <Field label={f.kind === 'role' ? 'Роль' : 'Сотрудник'}>
              <Select value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })}>
                <option value="">выберите</option>
                {(f.kind === 'role' ? subjects.data?.roles : subjects.data?.users)?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Предел суммы, ₸" hint="Пусто — по сумме без ограничения">
              <Input
                inputMode="decimal"
                value={f.amount}
                onChange={(e) => setF({ ...f, amount: e.target.value })}
              />
            </Field>
            {f.action === 'purchase_approval' ? (
              <Field label="Предел превышения сметы, %" hint="Пусто — без ограничения">
                <Input
                  inputMode="decimal"
                  value={f.deviation}
                  onChange={(e) => setF({ ...f, deviation: e.target.value })}
                />
              </Field>
            ) : null}
            <div className="sm:col-span-2">
              <Button
                type="submit"
                variant="primary"
                loading={put.isPending}
                disabled={!f.subject || (!f.amount.trim() && !f.deviation.trim())}
              >
                Задать лимит
              </Button>
            </div>
            <div className="sm:col-span-2">
              <ErrorBox error={put.error ?? del.error} />
            </div>
          </form>
        ) : null}
      </Panel>

      <Panel
        title="Сверка счёта с договором и приходом"
        meta="ФН-20: допуск по цене и количеству; по умолчанию 0 %"
      >
        {curTol ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Checkbox
              label="Сверка включена"
              checked={curTol.enabled}
              disabled={!match.data?.can.edit}
              onChange={(e) => setTol({ ...curTol, enabled: e.target.checked })}
            />
            <Field label="Допуск по цене, %">
              <Input
                inputMode="decimal"
                disabled={!match.data?.can.edit}
                value={curTol.pricePct}
                onChange={(e) => setTol({ ...curTol, pricePct: e.target.value })}
              />
            </Field>
            <Field label="Допуск по количеству, %">
              <Input
                inputMode="decimal"
                disabled={!match.data?.can.edit}
                value={curTol.qtyPct}
                onChange={(e) => setTol({ ...curTol, qtyPct: e.target.value })}
              />
            </Field>
            {match.data?.can.edit ? (
              <div className="sm:col-span-3">
                <Button
                  variant="primary"
                  loading={putTol.isPending}
                  disabled={!tol}
                  onClick={() => tol && putTol.mutate(tol)}
                >
                  Сохранить допуск
                </Button>
                <ErrorBox error={putTol.error} />
              </div>
            ) : (
              <p className="text-[13px] text-ink-2 sm:col-span-3">Допуск задаёт администратор портала.</p>
            )}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

/* ───────────────────────── Выгрузка в 1С (ИН-05) ───────────────────────── */

const FIELD_KEYS = Object.keys(EXPORT_FIELDS) as ExportField[];
const DEFAULT_PROFILE: ExportProfileInput = {
  name: '',
  format: 'csv',
  delimiter: ';',
  locale: 'ru',
  columns: [
    { field: 'number', header: 'Номер заявки' },
    { field: 'plannedDate', header: 'Дата оплаты' },
    { field: 'counterpartyName', header: 'Контрагент' },
    { field: 'counterpartyBin', header: 'БИН контрагента' },
    { field: 'contractNumber', header: 'Договор' },
    { field: 'invoiceNumber', header: 'Номер счёта' },
    { field: 'amount', header: 'Сумма' },
    { field: 'purpose', header: 'Назначение платежа' },
    { field: 'id', header: 'Идентификатор портала' },
  ],
  statuses: ['sent'],
  active: true,
};

/** Файл выгрузки: POST меняет состояние (заявки помечаются выгруженными), поэтому скачиваем запросом, а не ссылкой */
async function downloadExport(body: object): Promise<{ name: string; rows: number }> {
  const res = await fetch('/api/v1/finance/export-1c', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'x-epc-csrf': '1', 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(j?.error?.message ?? `Ошибка ${res.status}`);
  }
  const type = res.headers.get('content-type') ?? '';
  const blob = type.includes('json')
    ? new Blob([JSON.stringify(await res.json(), null, 1)], { type: 'application/json' })
    : await res.blob();
  const ext = type.includes('json') ? 'json' : type.includes('spreadsheet') ? 'xlsx' : 'csv';
  const name = `1c-zayavki-${new Date().toISOString().slice(0, 10)}.${ext}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { name, rows: 0 };
}

function ProfileForm({
  initial,
  onDone,
}: {
  initial: ExportProfileInput & { id?: string };
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [p, setP] = useState(initial);
  const save = useMutation({
    mutationFn: () => {
      const { id, ...body } = p;
      return api<ExportProfileView>(`/finance/export-profiles${id ? `/${id}` : ''}`, {
        method: id ? 'PUT' : 'POST',
        body,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance', 'export-profiles'] });
      toast('Профиль сохранён');
      onDone();
    },
  });
  const setCol = (i: number, patch: Partial<ExportColumn>) =>
    setP({ ...p, columns: p.columns.map((c, k) => (k === i ? { ...c, ...patch } : c)) });
  const move = (i: number, d: -1 | 1) => {
    const cols = [...p.columns];
    const j = i + d;
    if (j < 0 || j >= cols.length) return;
    [cols[i], cols[j]] = [cols[j]!, cols[i]!];
    setP({ ...p, columns: cols });
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Название профиля" className="sm:col-span-2">
          <Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} />
        </Field>
        <Field label="Формат файла">
          <Select
            value={p.format}
            onChange={(e) => setP({ ...p, format: e.target.value as 'csv' | 'xlsx' | 'json' })}
          >
            {Object.entries(EXPORT_FORMATS).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Разделитель CSV">
          <Select
            value={p.delimiter}
            onChange={(e) => setP({ ...p, delimiter: e.target.value as ';' | ',' | '\t' })}
          >
            <option value=";">точка с запятой</option>
            <option value=",">запятая</option>
            <option value={'\t'}>табуляция</option>
          </Select>
        </Field>
        <Field
          label="Числа и даты"
          hint="Русский вид: 1234,50 и 31.12.2026; международный: 1234.50 и 2026-12-31"
        >
          <Select value={p.locale} onChange={(e) => setP({ ...p, locale: e.target.value as 'ru' | 'iso' })}>
            <option value="ru">русский</option>
            <option value="iso">международный</option>
          </Select>
        </Field>
        <Field label="Какие заявки уходят">
          <Select
            value={p.statuses.join(',')}
            onChange={(e) =>
              setP({ ...p, statuses: e.target.value.split(',') as ExportProfileInput['statuses'] })
            }
          >
            <option value="sent">переданные на оплату</option>
            <option value="sent,paid">переданные и оплаченные</option>
            <option value="paid">оплаченные</option>
          </Select>
        </Field>
      </div>
      <p className="text-[13px] text-ink-2">
        Колонки файла по порядку. Поля обмена с бухгалтерией и программистом 1С ещё не согласованы: порядок и
        заголовки задайте под файл, который ждёт 1С.
      </p>
      {p.columns.map((c, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: порядок колонок и есть ключ
          key={i}
          className="grid grid-cols-[1fr_auto] gap-2 rounded-md border border-line p-2 sm:grid-cols-[14rem_1fr_auto]"
        >
          <Select
            aria-label="Поле портала"
            value={c.field}
            onChange={(e) => setCol(i, { field: e.target.value as ExportField })}
          >
            {FIELD_KEYS.map((k) => (
              <option key={k} value={k}>
                {EXPORT_FIELDS[k].label}
              </option>
            ))}
          </Select>
          <Input
            aria-label="Заголовок колонки"
            placeholder="Заголовок колонки в файле"
            value={c.header}
            onChange={(e) => setCol(i, { header: e.target.value })}
            className="col-span-2 sm:col-span-1 sm:order-none"
          />
          <div className="flex gap-1">
            <Button variant="ghost" aria-label="Выше" onClick={() => move(i, -1)}>
              <ArrowUp className="size-4" aria-hidden />
            </Button>
            <Button variant="ghost" aria-label="Ниже" onClick={() => move(i, 1)}>
              <ArrowDown className="size-4" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              aria-label="Убрать колонку"
              onClick={() => setP({ ...p, columns: p.columns.filter((_, k) => k !== i) })}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          </div>
          {c.field === 'constant' ? (
            <Input
              aria-label="Постоянное значение"
              placeholder="Постоянное значение во всех строках"
              className="col-span-2 sm:col-span-3"
              value={c.value ?? ''}
              onChange={(e) => setCol(i, { value: e.target.value })}
            />
          ) : null}
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button
          icon={<Plus className="size-4" aria-hidden />}
          onClick={() =>
            setP({
              ...p,
              columns: [...p.columns, { field: 'amount', header: `Колонка ${p.columns.length + 1}` }],
            })
          }
        >
          Добавить колонку
        </Button>
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={p.name.trim().length < 2 || !p.columns.length || p.columns.some((c) => !c.header.trim())}
          onClick={() => save.mutate()}
        >
          Сохранить профиль
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Отмена
        </Button>
      </div>
      <ErrorBox error={save.error} />
    </div>
  );
}

export function Export1cView() {
  const me = useMe();
  const qc = useQueryClient();
  const money = seesMoney(me.access, 'acts_invoices');
  const profiles = useQuery({
    queryKey: ['finance', 'export-profiles'],
    queryFn: () => api<ExportProfileView[]>('/finance/export-profiles'),
    enabled: money,
    retry: false,
  });
  const [pid, setPid] = useState('');
  const [again, setAgain] = useState(false);
  const [editing, setEditing] = useState<(ExportProfileInput & { id?: string }) | null>(null);
  const active = (profiles.data ?? []).filter((p) => p.active);
  const cur = pid || active[0]?.id || '';
  const preview = useQuery({
    queryKey: ['finance', 'export-1c', 'preview', cur, again],
    queryFn: () =>
      api<ExportPreview>('/finance/export-1c/preview', {
        query: { profileId: cur, includeExported: again ? '1' : '0' },
      }),
    enabled: Boolean(cur),
  });
  const batches = useQuery({
    queryKey: ['finance', 'export-1c', 'batches'],
    queryFn: () => api<ExportBatchView[]>('/finance/export-1c/batches'),
    enabled: money && !profiles.isError,
  });
  const run = useMutation({
    mutationFn: () => downloadExport({ profileId: cur, includeExported: again ? '1' : '0' }),
    onSuccess: () => {
      toast('Файл выгружен, заявки помечены выгруженными');
      void qc.invalidateQueries({ queryKey: ['finance'] });
      void qc.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
  const revert = useMutation({
    mutationFn: (id: string) => api(`/finance/export-1c/batches/${id}/revert`, { method: 'POST', body: {} }),
    onSuccess: () => {
      toast('Заявки пачки снова готовы к выгрузке');
      void qc.invalidateQueries({ queryKey: ['finance'] });
    },
  });
  if (!money)
    return (
      <EmptyState title="Нужен уровень денег «Акты и счета»">
        Выгрузка в 1С — с этим уровнем денег.
      </EmptyState>
    );
  if (profiles.isError)
    return (
      <EmptyState title="Нет доступа к выгрузке в 1С">
        Выгрузку ведёт сотрудник с правом «Выгрузка в 1С». Доступ выдаёт администратор портала.
      </EmptyState>
    );
  if (profiles.isLoading) return <Spinner />;
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Выгрузка заявок на оплату"
        meta="ИН-05, этап 1: файл для «Загрузки данных из табличного документа» или ответ API для обмена"
        actions={
          <Button
            icon={<Plus className="size-4" aria-hidden />}
            onClick={() => setEditing({ ...DEFAULT_PROFILE, name: '' })}
          >
            Новый профиль
          </Button>
        }
      >
        {editing ? (
          <ProfileForm key={editing.id ?? 'new'} initial={editing} onDone={() => setEditing(null)} />
        ) : active.length ? (
          <div className="flex flex-col gap-3">
            <Field
              label="Профиль выгрузки"
              hint="Какие поля, в каком порядке и как названы колонки — настраивается в профиле"
            >
              <Select value={cur} onChange={(e) => setPid(e.target.value)}>
                {active.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {EXPORT_FORMATS[p.format]}
                  </option>
                ))}
              </Select>
            </Field>
            <Checkbox
              label="Включать уже выгруженные ранее"
              checked={again}
              onChange={(e) => setAgain(e.target.checked)}
            />
            {preview.data ? (
              <div className="rounded-md bg-tile px-3 py-2 text-[14px]">
                К выгрузке: <b>{preview.data.rows}</b> заявок
                {preview.data.total !== null ? ` на ${tenge(preview.data.total)}` : ''}. Уже выгружено ранее:{' '}
                {preview.data.alreadyExported}.
                {preview.data.skipped.length ? (
                  <ul className="mt-1 list-disc pl-5 text-[13px] text-ink-2">
                    {preview.data.skipped.slice(0, 5).map((s) => (
                      <li key={s.number}>
                        {s.number}: {s.reason}
                      </li>
                    ))}
                    {preview.data.skipped.length > 5 ? (
                      <li>и ещё {preview.data.skipped.length - 5}</li>
                    ) : null}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                icon={<Download className="size-4" aria-hidden />}
                loading={run.isPending}
                disabled={!cur || preview.data?.rows === 0}
                onClick={() => run.mutate()}
              >
                Выгрузить
              </Button>
              <Button
                onClick={() => {
                  const p = active.find((x) => x.id === cur);
                  if (p) setEditing({ ...p });
                }}
              >
                Настроить профиль
              </Button>
            </div>
            <ErrorBox error={run.error} />
          </div>
        ) : (
          <EmptyState title="Профилей выгрузки пока нет">
            Создайте профиль: выберите поля портала и назовите колонки так, как их ждёт 1С.
          </EmptyState>
        )}
      </Panel>
      <Panel title="Журнал выгрузок" meta="Кто, когда и сколько заявок выгрузил">
        {batches.data?.length ? (
          <ul className="divide-y divide-line">
            {batches.data.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[14px]">
                <div>
                  <div className="font-medium">
                    {b.profileName} · {b.rows} заявок{b.total !== null ? ` на ${tenge(b.total)}` : ''}
                  </div>
                  <div className="text-[13px] text-ink-2">
                    {dateTime(b.at)} · {b.byName ?? '—'}
                  </div>
                </div>
                <Button variant="ghost" loading={revert.isPending} onClick={() => revert.mutate(b.id)}>
                  Вернуть к выгрузке
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[14px] text-ink-2">Выгрузок ещё не было.</p>
        )}
        <ErrorBox error={revert.error} />
      </Panel>
    </div>
  );
}
